import * as React from 'react'

import {
  AlertDialog,
  ArrowDownIcon,
  ArrowLeftIcon,
  Button,
  ChevronDownIcon,
  XIcon,
  CriticalIcon,
  Dialog,
  DropdownMenu,
  EmptyState,
  Flex,
  IconButton,
  Loader,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  ScrollArea,
  toast,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { logError } from '~publish/legacy/utils/logError'

import { ChangelogEntryCard } from './ChangelogEntryCard'
import { ChangelogEntryContent } from './ChangelogEntryContent'
import {
  ChangelogEntryEditor,
  deserializeChangelogHtml,
} from './ChangelogEntryEditor'
import type { ChangelogEntryData } from './ChangelogEntryEditor'
import {
  useChangelogEntries,
  useChangelogEntry,
  useCreateChangelogEntry,
  useDeleteChangelogEntry,
  useLatestNewEntryId,
  useUpdateChangelogEntry,
} from './useChangelog'
import type { ChangelogEntryData as ChangelogEntryQueryData } from './useChangelog'
import { useChangelogTracking } from './useChangelogTracking'
import { useIsChangelogManager } from './useIsChangelogManager'

import styles from './ChangelogDialog.module.css'

import type { JSX } from 'react'

interface ChangelogDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Current entry ID to display, undefined for list view */
  entryId?: string
  /** Callback when navigation changes the entry (null = list view) */
  onEntryIdChange: (entryId: string | null) => void
}

/**
 * Create view - uses ChangelogEntryEditor with integrated Edit/Preview
 */
function CreateView({
  onBackToList,
  onSave,
}: {
  onBackToList: () => void
  onSave: (data: ChangelogEntryData) => void
}): JSX.Element {
  return (
    <ChangelogEntryEditor onSave={onSave} onCancel={onBackToList}>
      <Dialog.Header>
        <Flex align="center" justify="between" className={styles.entryHeader}>
          <IconButton
            variant="tertiary"
            size="medium"
            label="Back to list"
            onClick={onBackToList}
          >
            <ArrowLeftIcon />
          </IconButton>
          <Dialog.Title className={styles.centeredTitle}>
            Create New
          </Dialog.Title>
          <Dialog.Close>
            <IconButton variant="tertiary" size="medium" label="Close">
              <XIcon />
            </IconButton>
          </Dialog.Close>
        </Flex>
      </Dialog.Header>
    </ChangelogEntryEditor>
  )
}

/**
 * Edit view - uses ChangelogEntryEditor with initial data for editing
 */
function EditView({
  entry,
  onBackToEntry,
  onSave,
}: {
  entry: ChangelogEntryQueryData
  onBackToEntry: () => void
  onSave: (data: ChangelogEntryData) => void
}): JSX.Element {
  // Deserialize the stored HTML back to Slate format for editing
  const slateValue = React.useMemo(
    () => deserializeChangelogHtml(entry.content),
    [entry.content],
  )

  return (
    <ChangelogEntryEditor
      initialData={{
        title: entry.title,
        content: {
          html: entry.content,
          slateValue,
        },
        coverImageUrl:
          typeof entry.coverImageUrl === 'string'
            ? entry.coverImageUrl
            : undefined,
        audience: entry.audience ?? 'general',
        publishedAt: entry.publishedAt
          ? new Date(entry.publishedAt)
          : undefined,
        slug: entry.slug,
      }}
      onSave={onSave}
      onCancel={onBackToEntry}
    >
      <Dialog.Header>
        <Flex align="center" justify="between" className={styles.entryHeader}>
          <IconButton
            variant="tertiary"
            size="medium"
            label="Back to entry"
            onClick={onBackToEntry}
          >
            <ArrowLeftIcon />
          </IconButton>
          <Dialog.Title className={styles.centeredTitle}>
            Edit Entry
          </Dialog.Title>
          <Dialog.Close>
            <IconButton variant="tertiary" size="medium" label="Close">
              <XIcon />
            </IconButton>
          </Dialog.Close>
        </Flex>
      </Dialog.Header>
    </ChangelogEntryEditor>
  )
}

/**
 * Entry view - shows a single changelog entry
 */
function EntryView({
  entryId,
  onBackToList,
  onEdit,
}: {
  entryId: string
  onBackToList: () => void
  onEdit: () => void
}): JSX.Element {
  const { entry, loading, error } = useChangelogEntry(entryId)
  const latestNewEntryId = useLatestNewEntryId()
  const [deleteEntry] = useDeleteChangelogEntry()
  const { canManage } = useIsChangelogManager()
  const { trackChangelogEntryViewed } = useChangelogTracking()
  const loadedEntryId = entry?.id
  const loadedEntryTitle = entry?.title

  // Track entry view when entry is loaded
  React.useEffect(() => {
    if (
      loadedEntryId !== undefined &&
      loadedEntryTitle !== undefined &&
      !loading
    ) {
      trackChangelogEntryViewed({ id: loadedEntryId, title: loadedEntryTitle })
    }
  }, [loadedEntryId, loadedEntryTitle, loading, trackChangelogEntryViewed])

  const handleDelete = async (): Promise<void> => {
    const result = await deleteEntry({
      variables: {
        input: { id: entryId },
      },
    })

    if (result.success) {
      toast.success('Changelog entry deleted.')
      onBackToList()
    } else {
      logError(result.error, { context: 'deleteChangelogEntry' })
      toast.error(result.error?.message ?? 'Failed to delete changelog entry.')
    }
  }

  return (
    <>
      <Dialog.Header>
        <Flex align="center" justify="between" className={styles.entryHeader}>
          <IconButton
            variant="tertiary"
            size="medium"
            label="Back to list"
            onClick={onBackToList}
          >
            <ArrowLeftIcon />
          </IconButton>
          <Dialog.Title className={styles.centeredTitle}>
            What&apos;s New
          </Dialog.Title>
          <Dialog.Close>
            <IconButton variant="tertiary" size="medium" label="Close">
              <XIcon />
            </IconButton>
          </Dialog.Close>
        </Flex>
      </Dialog.Header>

      <Dialog.Body className={styles.scrollBody}>
        <ScrollArea
          className={`${styles.scrollArea} ${styles.entryScrollArea}`}
          gradientFade="var(--space-xl)"
          visibility="auto"
        >
          <div
            className={`${styles.scrollBodyContent} ${styles.entryScrollBodyContent}`}
          >
            {loading ? (
              <Flex justify="center">
                <Loader />
              </Flex>
            ) : error ? (
              <EmptyState size="medium" variant="critical">
                <EmptyState.Icon>
                  <CriticalIcon />
                </EmptyState.Icon>
                <EmptyState.Heading>Error loading entry</EmptyState.Heading>
                <EmptyState.Description>{error.message}</EmptyState.Description>
              </EmptyState>
            ) : entry ? (
              <>
                <ChangelogEntryContent
                  title={entry.title}
                  content={entry.content}
                  coverImageUrl={
                    typeof entry.coverImageUrl === 'string'
                      ? entry.coverImageUrl
                      : null
                  }
                  publishedAt={entry.publishedAt}
                  author={entry.author}
                  audience={entry.audience}
                  isNew={entry.id === latestNewEntryId}
                />
                {canManage && (
                  <div className={styles.manageSection}>
                    <AlertDialog>
                      <DropdownMenu>
                        <DropdownMenu.Trigger>
                          <Button variant="tertiary">
                            Manage
                            <ChevronDownIcon />
                          </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content>
                          <DropdownMenu.Item onSelect={onEdit}>
                            <PencilIcon />
                            Edit
                          </DropdownMenu.Item>
                          <AlertDialog.Trigger>
                            <DropdownMenu.Item variant="critical">
                              <TrashIcon />
                              Permanently delete
                            </DropdownMenu.Item>
                          </AlertDialog.Trigger>
                        </DropdownMenu.Content>
                      </DropdownMenu>
                      <AlertDialog.Content>
                        <AlertDialog.Title>
                          Delete changelog entry?
                        </AlertDialog.Title>
                        <AlertDialog.Description>
                          This will permanently delete &quot;{entry.title}
                          &quot;. This action cannot be undone.
                        </AlertDialog.Description>
                        <AlertDialog.Actions>
                          <AlertDialog.Cancel>
                            <Button variant="tertiary">Cancel</Button>
                          </AlertDialog.Cancel>
                          <AlertDialog.Action>
                            <Button variant="critical" onClick={handleDelete}>
                              Delete
                            </Button>
                          </AlertDialog.Action>
                        </AlertDialog.Actions>
                      </AlertDialog.Content>
                    </AlertDialog>
                  </div>
                )}
              </>
            ) : (
              <EmptyState size="medium">
                <EmptyState.Icon>
                  <SearchIcon />
                </EmptyState.Icon>
                <EmptyState.Heading>Entry not found</EmptyState.Heading>
                <EmptyState.Description>
                  This changelog entry may have been removed or is no longer
                  available.
                </EmptyState.Description>
              </EmptyState>
            )}
          </div>
        </ScrollArea>
      </Dialog.Body>
    </>
  )
}

/**
 * List view - shows all changelog entries
 */
function ListView({
  onEntryClick,
  onCreateClick,
}: {
  onEntryClick: (entryId: string) => void
  onCreateClick: () => void
}): JSX.Element {
  const { entries, loading, error, hasMore, loadMore } = useChangelogEntries()
  const latestNewEntryId = useLatestNewEntryId()
  const { canManage: canCreateEntries } = useIsChangelogManager()
  const { trackChangelogEntryListViewed } = useChangelogTracking()

  // Track list view when entries are loaded
  React.useEffect(() => {
    if (!loading && entries.length > 0) {
      trackChangelogEntryListViewed()
    }
  }, [loading, entries.length, trackChangelogEntryListViewed])

  return (
    <>
      <Dialog.Header>
        <Flex align="center" justify="between">
          <Dialog.Title className={styles.listTitle}>
            What&apos;s New
          </Dialog.Title>
          <Flex gap="xs" align="center">
            {canCreateEntries && (
              <IconButton
                variant="primary"
                label="New entry"
                onClick={onCreateClick}
              >
                <PlusIcon />
              </IconButton>
            )}
            {/* TODO: Enable once marketing site changelog is live
            <Button
              variant="secondary"
              as="a"
              href="https://buffer.com/changelog"
              target="_blank"
              rel="noopener noreferrer"
            >
              Changelog
              <ArrowUpRightIcon />
            </Button>
*/}
            <Dialog.Close>
              <IconButton variant="tertiary" size="medium" label="Close">
                <XIcon />
              </IconButton>
            </Dialog.Close>
          </Flex>
        </Flex>
      </Dialog.Header>

      <Dialog.Body className={styles.scrollBody}>
        <ScrollArea
          className={styles.scrollArea}
          flashOnMount
          visibility="scroll"
        >
          <div className={styles.scrollBodyContent}>
            {loading && entries.length === 0 ? (
              <ul className={styles.entriesList}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <li key={i}>
                    <ChangelogEntryCard.Skeleton />
                  </li>
                ))}
              </ul>
            ) : error ? (
              <EmptyState size="small" variant="critical">
                <EmptyState.Icon>
                  <CriticalIcon />
                </EmptyState.Icon>
                <EmptyState.Heading>Error loading entries</EmptyState.Heading>
                <EmptyState.Description>{error.message}</EmptyState.Description>
              </EmptyState>
            ) : entries.length === 0 ? (
              <EmptyState size="small">
                <EmptyState.Icon>
                  <SearchIcon />
                </EmptyState.Icon>
                <EmptyState.Heading>No entries yet</EmptyState.Heading>
                <EmptyState.Description>
                  Check back later for updates and announcements.
                </EmptyState.Description>
              </EmptyState>
            ) : (
              <>
                <ul className={styles.entriesList}>
                  {entries.map((entry) => (
                    <li key={entry.id}>
                      <ChangelogEntryCard
                        entry={entry}
                        onClick={onEntryClick}
                        isNew={entry.id === latestNewEntryId}
                      />
                    </li>
                  ))}
                </ul>
                {hasMore && (
                  <Flex justify="center">
                    <Button
                      variant="tertiary"
                      onClick={(e): void => {
                        e.stopPropagation()
                        loadMore()
                      }}
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Load more'}
                      <ArrowDownIcon />
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </Dialog.Body>
    </>
  )
}

/**
 * Unified changelog dialog that can display either a list of entries,
 * a single entry view, or the create view.
 *
 * View state is derived from URL (via entryId prop):
 * - entryId undefined: list view
 * - entryId defined: entry view
 * - isCreating (internal): create view
 */
export function ChangelogDialog({
  open,
  onOpenChange,
  entryId,
  onEntryIdChange,
}: ChangelogDialogProps): JSX.Element {
  // Create/Edit mode is internal state (not in URL)
  const [isCreating, setIsCreating] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)

  const [createEntry] = useCreateChangelogEntry()
  const [updateEntry] = useUpdateChangelogEntry()

  // Fetch entry data for editing
  const { entry: editingEntry } = useChangelogEntry(
    isEditing && entryId ? entryId : null,
  )

  // Reset modes when dialog closes
  React.useEffect(() => {
    if (!open) {
      setIsCreating(false)
      setIsEditing(false)
    }
  }, [open])

  const handleEntryClick = (id: string): void => {
    onEntryIdChange(id)
  }

  const handleBackToList = (): void => {
    setIsCreating(false)
    setIsEditing(false)
    onEntryIdChange(null)
  }

  const handleBackToEntry = (): void => {
    setIsEditing(false)
  }

  const handleCreateClick = (): void => {
    setIsCreating(true)
  }

  const handleEditClick = (): void => {
    setIsEditing(true)
  }

  const handleSaveEntry = async (data: ChangelogEntryData): Promise<void> => {
    const result = await createEntry({
      variables: {
        input: {
          title: data.title,
          content: data.content.html,
          coverImageUrl: data.coverImageUrl,
          audience: data.audience,
          publishedAt: data.publishedAt?.toISOString(),
          slug: data.slug,
        },
      },
    })

    if (result.success) {
      toast.success('Changelog entry created!')
      setIsCreating(false)
      onEntryIdChange(null)
    } else {
      logError(result.error, { context: 'createChangelogEntry' })
      toast.error(result.error?.message ?? 'Failed to create changelog entry.')
    }
  }

  const handleUpdateEntry = async (data: ChangelogEntryData): Promise<void> => {
    if (!entryId) return

    const result = await updateEntry({
      variables: {
        input: {
          id: entryId,
          title: data.title,
          content: data.content.html,
          coverImageUrl: data.coverImageUrl,
          audience: data.audience,
          publishedAt: data.publishedAt?.toISOString(),
          slug: data.slug,
        },
      },
    })

    if (result.success) {
      toast.success('Changelog entry updated!')
      setIsEditing(false)
    } else {
      logError(result.error, { context: 'updateChangelogEntry' })
      toast.error(result.error?.message ?? 'Failed to update changelog entry.')
    }
  }

  const renderView = (): JSX.Element => {
    if (isCreating) {
      return (
        <CreateView onBackToList={handleBackToList} onSave={handleSaveEntry} />
      )
    }
    if (isEditing && editingEntry) {
      return (
        <EditView
          entry={editingEntry}
          onBackToEntry={handleBackToEntry}
          onSave={handleUpdateEntry}
        />
      )
    }
    if (entryId) {
      return (
        <EntryView
          entryId={entryId}
          onBackToList={handleBackToList}
          onEdit={handleEditClick}
        />
      )
    }
    return (
      <ListView
        onEntryClick={handleEntryClick}
        onCreateClick={handleCreateClick}
      />
    )
  }

  // Block accidental dismissal (outside-click / Escape) while the user has the
  // create or edit form open — explicit Cancel/X still close the dialog.
  const isEditingEntry = isCreating || isEditing
  const blockIfEditing = (event: Event): void => {
    if (isEditingEntry) event.preventDefault()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        size="medium"
        className={styles.dialog}
        onInteractOutside={blockIfEditing}
        onEscapeKeyDown={blockIfEditing}
      >
        {renderView()}
      </Dialog.Content>
    </Dialog>
  )
}
